import { useState, useEffect } from "react"
import {
    Typography,
    Container,
    Box,
    AppBar,
    Toolbar,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { ArrowBack } from "@mui/icons-material"
import React from "react"


// Terms section component using Material UI
const TermsSection = ({ title, content }) => {
    return (
        <Card
            sx={{
                backgroundColor: "rgba(15, 23, 42, 0.5)",
                borderColor: "rgba(30, 41, 59, 0.8)",
                borderRadius: 1,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                transition: "box-shadow 0.2s",
                "&:hover": {
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                },
            }}
            variant="outlined"
        >
            <CardHeader
                title={
                    <Typography variant="h6" sx={{ color: "rgba(241, 245, 249, 0.9)", fontWeight: 600 }}>
                        {title}
                    </Typography>
                }
                sx={{ pb: 1 }}
            />
            <Divider sx={{ mx: 3, backgroundColor: "rgba(71, 85, 105, 0.8)" }} />
            <CardContent sx={{ pt: 2 }}>
                <Box sx={{ color: "rgba(203, 213, 225, 0.9)" }}>
                    {typeof content === "string" ? (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            {content}
                        </Typography>
                    ) : (
                        content
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}

const TermsOfServicePage = () => {
    const [currentDate, setCurrentDate] = useState("")
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const navigate = useNavigate()

    useEffect(() => {
        setCurrentDate(new Date().toLocaleDateString())
    }, [])

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "none",
                color: "rgba(226, 232, 240, 0.9)",
                display: "flex",
                flexDirection: "column",
                zIndex: 0
            }}
        >
            {/* Sticky Header */}
            <AppBar
                position="sticky"
                sx={{
                    backgroundColor: "rgba(10, 15, 21, 0)",
                    boxShadow: "none",
                    zIndex: 1
                }}
            >
                <Toolbar>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => window.history.back()}
                        sx={{
                            color: "rgba(226, 232, 240, 0.9)",
                            "&:hover": {
                                backgroundColor: "rgba(30, 41, 59, 0.5)",
                            },
                        }}
                    >
                        Back
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container
                maxWidth="md"
                sx={{
                    pb: { xs: 4, md: 6 },
                    px: { xs: 2, md: 3 },
                    flexGrow: 1,
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {/* Header Section */}
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                color: "white",
                                mb: 1,
                            }}
                        >
                            Terms of Service
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(148, 163, 184, 0.9)" }}>
                            Last updated: {currentDate}
                        </Typography>
                    </Box>

                    {/* Introduction */}
                    <Card
                        sx={{
                            backgroundColor: "rgba(15, 23, 42, 0.5)",
                            borderColor: "rgba(30, 41, 59, 0.8)",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        }}
                        variant="outlined"
                    >
                        <CardContent>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "rgba(203, 213, 225, 0.9)",
                                    lineHeight: 1.7,
                                }}
                            >
                                Please read these Terms of Service carefully before using Time Capsule. By using our service, you agree
                                to be bound by these terms.
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Terms Sections */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <TermsSection
                            title="Acceptance of Terms"
                            content="By accessing or using Time Capsule, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using the service."
                        />

                        <TermsSection
                            title="User Accounts"
                            content={
                                <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.5 } }}>
                                    <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                        You are responsible for maintaining the confidentiality of your account
                                    </Typography>
                                    <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                        You must be at least 13 years old to use this service
                                    </Typography>
                                    <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                        You agree to provide accurate and complete information
                                    </Typography>
                                    <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                        You are responsible for all activities under your account
                                    </Typography>
                                </Box>
                            }
                        />

                        <TermsSection
                            title="Content Guidelines"
                            content={
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                                        You agree not to upload content that:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.5 } }}>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Is illegal or promotes illegal activities
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Infringes on others' intellectual property rights
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Contains malicious code or programs
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Violates others' privacy or publicity rights
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                        />

                        <TermsSection
                            title="Service Modifications"
                            content="We reserve the right to modify or discontinue the service at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the service."
                        />

                        <TermsSection
                            title="Limitation of Liability"
                            content="Time Capsule and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the service."
                        />

                        <TermsSection
                            title="Contact"
                            content={
                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                    For any questions about these Terms of Service, please{" "}
                                    <Box
                                        component="span"
                                        onClick={() => navigate("/contact")}
                                        sx={{
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            "&:hover": {
                                                color: "#FFF",
                                            },
                                        }}
                                    >
                                        contact us here
                                    </Box>
                                    .
                                </Typography>
                            }
                        />
                    </Box>
                </Box>
            </Container>

            {!isMobile &&
                <Box
                    component="footer"
                    sx={{
                        borderTop: "1px solid rgba(30, 41, 59, 0.8)",
                        py: 3,
                        mt: "auto",
                    }}
                >
                    <Container>
                        <Typography variant="body2" align="center" sx={{ color: "rgba(148, 163, 184, 0.9)" }}>
                            © {new Date().getFullYear()} Time Capsule. All rights reserved.
                        </Typography>
                    </Container>
                </Box>
            }
        </Box>
    )
}

export default TermsOfServicePage
